import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Plus, Bell, User, X, LogOut, Package, Heart, MessageCircle, Sun, Moon, Globe, Gavel, Shield, MapPin, ChevronDown, Calendar } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, useTheme, useLanguage, useCurrency, useIsland, useNotifications, useWebSocket, API, HEADER_LOGO_DARK, HEADER_LOGO_LIGHT } from '../App';
// Scroll to top utility
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * MobileBottomNav - Bottom navigation bar for mobile devices
 * Shows: Home, Search, Add (create listing), Notifications, Profile
 */
const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme(); // Use theme context
  const { language, setLanguage, t } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { currentIsland, setCurrentIsland, islands } = useIsland();
  const { unreadCount: contextUnreadNotifications } = useNotifications();
  const { unreadCount: wsUnreadMessages, lastMessage } = useWebSocket() || {};
  const [showProfileSheet, setShowProfileSheet] = useState(false);
  const [showIslandPicker, setShowIslandPicker] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  // Listen for sheet-open class on body to hide nav when sheet is open
  useEffect(() => {
    const checkSheetOpen = () => {
      setIsSheetOpen(document.body.classList.contains('sheet-open'));
    };
    
    // Check initially
    checkSheetOpen();
    
    // Use MutationObserver to watch for class changes on body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkSheetOpen();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    return () => observer.disconnect();
  }, []);
  const [comingSoonActive, setComingSoonActive] = useState(null); // null = unknown/loading

  // Check if Coming Soon mode is active
  useEffect(() => {
    const checkComingSoon = async () => {
      try {
        const response = await axios.get(`${API}/admin/coming-soon`);
        setComingSoonActive(response.data?.enabled === true);
      } catch (error) {
        setComingSoonActive(false);
      }
    };
    checkComingSoon();
  }, []);

  // Sync notifications with context for real-time updates
  useEffect(() => {
    if (contextUnreadNotifications !== undefined) {
      setUnreadNotifications(contextUnreadNotifications);
    }
  }, [contextUnreadNotifications]);

  // Sync messages with WebSocket for real-time updates
  useEffect(() => {
    if (wsUnreadMessages !== undefined) {
      setUnreadMessages(wsUnreadMessages);
    }
  }, [wsUnreadMessages]);

  // Fetch notification counts and admin status (NOT message count - that comes from WebSocket)
  const fetchCounts = useCallback(async () => {
    if (!user) return;
    try {
      const [notifRes, adminRes] = await Promise.all([
        axios.get(`${API}/notifications/unread/count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/users/me/is-admin`).catch(() => ({ data: { is_admin: false } }))
      ]);
      setUnreadNotifications(notifRes.data.count || 0);
      // Note: unreadMessages is managed by WebSocket context, not fetched here
      setIsAdmin(adminRes.data.is_admin || false);
    } catch (error) {
      // Silently fail
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCounts();
      const interval = setInterval(fetchCounts, 30000);
      
      // Listen for notification-read events
      const handleNotificationRead = () => {
        fetchCounts();
      };
      window.addEventListener('notification-read', handleNotificationRead);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('notification-read', handleNotificationRead);
      };
    }
  }, [user, token, fetchCounts]);

  // Refetch counts when navigating away from messages page (user may have read messages)
  useEffect(() => {
    if (location.pathname !== '/messages' && user) {
      fetchCounts();
    }
  }, [location.pathname, user, fetchCounts]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const [showGuestSheet, setShowGuestSheet] = useState(false);

  // Swipe gesture refs and state for profile sheets
  const profileSheetRef = useRef(null);
  const guestSheetRef = useRef(null);
  const dragStartY = useRef(0);
  const currentTranslateY = useRef(0);
  const isDragging = useRef(false);
  const activeSheet = useRef(null);

  // Handle touch start on drag handle
  const handleTouchStart = useCallback((sheetType) => (e) => {
    dragStartY.current = e.touches[0].clientY;
    isDragging.current = true;
    currentTranslateY.current = 0;
    activeSheet.current = sheetType;
    
    const ref = sheetType === 'guest' ? guestSheetRef : profileSheetRef;
    if (ref.current) {
      ref.current.style.transition = 'none';
    }
  }, []);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - dragStartY.current;
    
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      currentTranslateY.current = diff;
      const ref = activeSheet.current === 'guest' ? guestSheetRef : profileSheetRef;
      if (ref.current) {
        ref.current.style.transform = `translateY(${diff}px)`;
      }
    }
  }, []);

  // Handle touch end
  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    
    const ref = activeSheet.current === 'guest' ? guestSheetRef : profileSheetRef;
    const setShow = activeSheet.current === 'guest' ? setShowGuestSheet : setShowProfileSheet;
    
    if (ref.current) {
      ref.current.style.transition = 'transform 0.3s ease-out';
      
      // If dragged more than 100px down, close the sheet
      if (currentTranslateY.current > 100) {
        ref.current.style.transform = 'translateY(100%)';
        setTimeout(() => setShow(false), 300);
      } else {
        // Snap back to original position
        ref.current.style.transform = 'translateY(0)';
      }
    }
    currentTranslateY.current = 0;
    activeSheet.current = null;
  }, []);

  // Body scroll lock when profile sheet or island picker is open
  useEffect(() => {
    if (showProfileSheet || showIslandPicker || showGuestSheet) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showProfileSheet, showIslandPicker, showGuestSheet]);

  const handleLogout = () => {
    logout();
    setShowProfileSheet(false);
    navigate('/');
  };

  const handleProfileClick = () => {
    if (!user) {
      setShowGuestSheet(true);
    } else {
      setShowProfileSheet(true);
    }
  };

  const navItems = [
    { 
      isLogo: true, 
      labelKey: 'home', 
      path: '/',
      onClick: () => {
        if (location.pathname === '/') {
          scrollToTop();
        } else {
          navigate('/');
        }
      }
    },
    { 
      icon: Search, 
      labelKey: 'search', 
      path: '/marketplace',
      onClick: () => {
        // If already on marketplace, clear all filters
        if (location.pathname === '/marketplace') {
          setSearchParams({});
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          navigate('/marketplace');
        }
      }
    },
    { 
      icon: Plus, 
      labelKey: 'create', 
      path: '/create-listing',
      isAdd: true,
      onClick: () => {
        if (!user) {
          navigate('/auth');
        } else {
          navigate('/create-listing');
        }
      }
    },
    { 
      icon: MessageCircle, 
      labelKey: 'inbox', 
      path: '/messages',
      badge: unreadMessages,
      onClick: () => {
        if (!user) {
          navigate('/auth');
        } else {
          navigate('/messages');
        }
      }
    },
    { 
      icon: User, 
      labelKey: 'profile', 
      path: '/profile',
      badge: unreadNotifications, // Show unread notifications badge on profile
      onClick: handleProfileClick
    },
  ];

  // Hide on homepage when Coming Soon is active OR still loading (to prevent flash)
  if (location.pathname === '/') {
    // On homepage: hide if Coming Soon is active OR status is still unknown
    if (comingSoonActive === true || comingSoonActive === null) {
      return null;
    }
  }

  return (
    <>
      {/* Bottom Navigation Bar - Hidden when sheet is open */}
      {!isSheetOpen && (
        <nav 
          className="md:hidden fixed bottom-0 left-0 right-0 z-[200] bg-card border-t border-border"
          style={{ 
            position: 'fixed',
            bottom: 0,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
          data-testid="mobile-bottom-nav"
        >
          <div className="flex items-center justify-around h-16 px-2 bg-card/95 backdrop-blur-lg">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            if (item.isAdd) {
              // Special styling for Add/Sell button
              return (
                <button
                  key={item.labelKey}
                  onClick={item.onClick}
                  className="flex flex-col items-center justify-center -mt-4"
                  data-testid="mobile-nav-add"
                >
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <span className="text-[10px] text-muted-foreground mt-1">{t(item.labelKey)}</span>
                </button>
              );
            }
            
            if (item.isLogo) {
              // Logo for Home button
              return (
                <button
                  key={item.labelKey}
                  onClick={item.onClick}
                  className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-colors relative ${
                    active ? 'text-primary' : 'text-muted-foreground'
                  }`}
                  data-testid={`mobile-nav-${item.labelKey}`}
                >
                  <div className="relative">
                    <img 
                      src="/icon-home.png"
                      alt="Home"
                      className="w-6 h-6 object-contain rounded-md"
                    />
                  </div>
                  <span className={`text-[10px] mt-1 ${active ? 'text-primary font-medium' : ''}`}>
                    {t(item.labelKey)}
                  </span>
                  {active && (
                    <div className="absolute -bottom-2 w-1 h-1 rounded-full bg-primary" />
                  )}
                </button>
              );
            }
            
            return (
              <button
                key={item.labelKey}
                onClick={item.onClick}
                className={`flex flex-col items-center justify-center py-2 px-3 rounded-xl transition-colors relative ${
                  active ? 'text-primary' : 'text-muted-foreground'
                }`}
                data-testid={`mobile-nav-${item.labelKey}`}
              >
                <div className="relative">
                  <Icon className={`w-6 h-6 ${active ? 'text-primary' : ''}`} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-1 ${active ? 'font-medium text-primary' : ''}`}>
                  {t(item.labelKey)}
                </span>
                {active && (
                  <div className="absolute -bottom-2 w-1 h-1 rounded-full bg-primary" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
      )}

      {/* Profile Bottom Sheet */}
      {showProfileSheet && (
        <div className="md:hidden fixed inset-0 z-[200]" data-testid="mobile-profile-sheet">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowProfileSheet(false)}
          />
          
          {/* Sheet */}
          <div 
            ref={profileSheetRef}
            className="absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
          >
            {/* Drag Handle */}
            <div 
              className="sticky top-0 bg-card pt-3 pb-2 px-4 flex justify-center cursor-grab active:cursor-grabbing touch-none"
              onTouchStart={handleTouchStart('profile')}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>
            
            {/* User Info */}
            {user && (
              <div className="px-4 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-7 h-7 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">{user.business_name || user.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => setShowProfileSheet(false)}
                    className="p-2 hover:bg-secondary rounded-full"
                  >
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
            
            {/* Quick Links */}
            <div className="p-4 space-y-1">
              <Link
                to="/profile"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <User className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">My Profile</span>
              </Link>
              
              <Link
                to="/my-listings"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <Package className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">{t('my_listings')}</span>
              </Link>
              
              <Link
                to="/my-events"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">My Events</span>
              </Link>
              
              <Link
                to="/my-bids"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <Gavel className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">My Bids</span>
              </Link>
              
              <Link
                to="/favorites"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <Heart className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">{t('favorites')}</span>
              </Link>
              
              <Link
                to="/notifications"
                onClick={() => setShowProfileSheet(false)}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <Bell className="w-5 h-5 text-muted-foreground" />
                <span className="text-foreground">Notificaties</span>
                {unreadNotifications > 0 && (
                  <span className="ml-auto bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadNotifications}
                  </span>
                )}
              </Link>
              
              {isAdmin && (
                <Link
                  to="/backoffice"
                  onClick={() => setShowProfileSheet(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
                >
                  <Shield className="w-5 h-5 text-amber-500" />
                  <span className="text-foreground">Admin Dashboard</span>
                </Link>
              )}
            </div>
            
            {/* Settings Section */}
            <div className="px-4 pb-4 border-t border-border pt-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 mb-2">
                Settings
              </h4>
              
              {/* Island Selector */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">Location</span>
                </div>
                <div className="grid grid-cols-3 gap-1 bg-secondary rounded-lg p-1">
                  {[
                    { id: 'aruba', name: 'Aruba', flag: '🇦🇼' },
                    { id: 'bonaire', name: 'Bonaire', flag: '🇧🇶' },
                    { id: 'curacao', name: 'Curaçao', flag: '🇨🇼' }
                  ].map((island) => (
                    <button
                      key={island.id}
                      onClick={() => setCurrentIsland(island.id)}
                      className={`flex flex-col items-center px-2 py-2 rounded-md transition-colors ${
                        currentIsland === island.id 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                    >
                      <span className="text-lg">{island.flag}</span>
                      <span className="text-[10px] mt-0.5">{island.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary rounded-xl transition-colors"
              >
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? (
                    <Moon className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Sun className="w-5 h-5 text-muted-foreground" />
                  )}
                  <span className="text-foreground">Theme</span>
                </div>
                <span className="text-sm text-muted-foreground capitalize">{theme}</span>
              </button>
              
              {/* Currency Selector */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 text-center text-muted-foreground font-medium">$</span>
                  <span className="text-foreground">Currency</span>
                </div>
                <div className="flex bg-secondary rounded-lg p-0.5">
                  {['USD', 'XCG'].map((curr) => (
                    <button
                      key={curr}
                      onClick={() => setCurrency(curr)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        currency === curr 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Language Selector */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">Language</span>
                </div>
                <div className="flex bg-secondary rounded-lg p-0.5">
                  {[
                    { code: 'en', label: 'EN' },
                    { code: 'nl', label: 'NL' },
                    { code: 'es', label: 'ES' }
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`px-2 py-1.5 text-xs rounded-md transition-colors ${
                        language === lang.code 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Logout */}
            <div className="p-4 border-t border-border">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 text-red-500 rounded-xl hover:bg-red-500/20 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{t('logout')}</span>
              </button>
            </div>
            
            {/* Bottom spacing for safe area */}
            <div className="h-4" />
          </div>
        </div>
      )}

      {/* Guest Profile Sheet */}
      {showGuestSheet && (
        <div className="md:hidden fixed inset-0 z-[200]" data-testid="guest-profile-sheet">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowGuestSheet(false)}
          />
          
          {/* Sheet */}
          <div 
            ref={guestSheetRef}
            className="absolute bottom-0 left-0 right-0 bg-card rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 16px)' }}
          >
            {/* Drag Handle */}
            <div 
              className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
              onTouchStart={handleTouchStart('guest')}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
            </div>
            
            {/* Auth Buttons */}
            <div className="px-4 pb-4 space-y-3">
              <Link
                to="/auth"
                onClick={() => setShowGuestSheet(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 text-base font-semibold bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
              >
                {t('sign_in')}
              </Link>
              <Link
                to="/register"
                onClick={() => setShowGuestSheet(false)}
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 text-base font-medium border border-border text-foreground rounded-xl hover:bg-secondary transition-colors"
              >
                {t('get_started')}
              </Link>
            </div>
            
            {/* Settings Section */}
            <div className="border-t border-border">
              {/* Island Selector */}
              <div className="px-4 py-4">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">Location</span>
                </div>
                <div className="grid grid-cols-3 gap-1 bg-secondary rounded-lg p-1">
                  {[
                    { id: 'aruba', name: 'Aruba', flag: '🇦🇼' },
                    { id: 'bonaire', name: 'Bonaire', flag: '🇧🇶' },
                    { id: 'curacao', name: 'Curaçao', flag: '🇨🇼' }
                  ].map((island) => (
                    <button
                      key={island.id}
                      onClick={() => setCurrentIsland(island.id)}
                      data-testid={`guest-island-${island.id}`}
                      className={`flex flex-col items-center px-2 py-2 rounded-md transition-colors ${
                        currentIsland === island.id 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                    >
                      <span className="text-lg">{island.flag}</span>
                      <span className="text-[10px] mt-0.5">{island.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Theme Toggle */}
              <div className="flex items-center justify-between px-4 py-4 border-t border-border/50">
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? (
                    <Moon className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <Sun className="w-5 h-5 text-muted-foreground" />
                  )}
                  <span className="text-foreground">Theme</span>
                </div>
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                >
                  {theme === 'dark' ? (
                    <Sun className="w-5 h-5 text-amber-400" />
                  ) : (
                    <Moon className="w-5 h-5 text-slate-600" />
                  )}
                </button>
              </div>
              
              {/* Currency Selector */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <span className="text-foreground">Currency</span>
                <div className="flex bg-secondary rounded-lg p-0.5">
                  {[
                    { value: 'USD', label: '$ USD' },
                    { value: 'XCG', label: 'XCG' }
                  ].map((curr) => (
                    <button
                      key={curr.value}
                      onClick={() => setCurrency(curr.value)}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                        currency === curr.value 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {curr.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Language Selector */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-muted-foreground" />
                  <span className="text-foreground">Language</span>
                </div>
                <div className="flex bg-secondary rounded-lg p-0.5">
                  {[
                    { code: 'en', label: 'EN' },
                    { code: 'nl', label: 'NL' },
                    { code: 'es', label: 'ES' }
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className={`px-2 py-1.5 text-xs rounded-md transition-colors ${
                        language === lang.code 
                          ? 'bg-primary text-primary-foreground font-medium' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add bottom padding to main content when bottom nav is visible */}
      <style>{`
        @media (max-width: 767px) {
          main, .main-content {
            padding-bottom: 80px !important;
          }
        }
        
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0);
        }
      `}</style>
    </>
  );
};

export default MobileBottomNav;
